import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './Dashboard.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'approved', label: 'Approved' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });

  const fetchCounts = useCallback(async () => {
    try {
      const response = await api.get('/intake-admin/counts');
      setCounts(response.data.counts);
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/intake-admin?${params.toString()}`);
      setSubmissions(response.data.submissions);
    } catch (err) {
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCounts();
    fetchSubmissions();
  }, [fetchCounts, fetchSubmissions]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      submitted: 'status-badge status-submitted',
      reviewing: 'status-badge status-reviewing',
      approved: 'status-badge status-approved',
      converted: 'status-badge status-converted',
      rejected: 'status-badge status-rejected',
    };
    return <span className={statusClasses[status] || 'status-badge'}>{status}</span>;
  };

  return (
    <div className="intake-queue-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>League Intake Queue</h1>
          <p className="subtitle">Review and process league uniform order requests</p>
        </div>
      </div>

      <div className="stats-cards">
        <div className="stat-card stat-submitted">
          <div className="stat-value">{counts.submitted || 0}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card stat-reviewing">
          <div className="stat-value">{counts.reviewing || 0}</div>
          <div className="stat-label">In Review</div>
        </div>
        <div className="stat-card stat-approved">
          <div className="stat-value">{counts.approved || 0}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card stat-converted">
          <div className="stat-value">{counts.converted || 0}</div>
          <div className="stat-label">Converted</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by league name, submission #, or contact..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="submissions-table-container">
        {loading ? (
          <div className="loading">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📬</div>
            <h3>No Submissions Found</h3>
            <p>No intake submissions match your current filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <table className="submissions-table">
              <thead>
                <tr>
                  <th>Submission #</th>
                  <th>League</th>
                  <th>Contact</th>
                  <th>Season</th>
                  <th>Teams</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(sub => (
                  <tr key={sub.id} onClick={() => navigate(`/intake-queue/${sub.id}`)}>
                    <td className="submission-number">{sub.submission_number}</td>
                    <td>{sub.league_name}</td>
                    <td>
                      <div>{sub.contact_name}</div>
                      <div className="contact-email">{sub.contact_email}</div>
                    </td>
                    <td>{sub.season || '-'}</td>
                    <td>{sub.total_teams}</td>
                    <td>{getStatusBadge(sub.status)}</td>
                    <td>{formatDate(sub.created_at)}</td>
                    <td>
                      <button className="btn-view">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="submissions-mobile-list">
              {submissions.map(sub => (
                <div
                  key={sub.id}
                  className="submission-card"
                  onClick={() => navigate(`/intake-queue/${sub.id}`)}
                >
                  <div className="submission-card-header">
                    <span className="submission-card-number">{sub.submission_number}</span>
                    {getStatusBadge(sub.status)}
                  </div>
                  <div className="submission-card-body">
                    <div className="submission-card-league">{sub.league_name}</div>
                    <div className="submission-card-contact">
                      {sub.contact_name} - {sub.contact_email}
                    </div>
                    <div className="submission-card-row">
                      <span className="submission-card-meta">
                        {sub.season || 'No season'} • {formatDate(sub.created_at)}
                      </span>
                      <span className="submission-card-teams">{sub.total_teams} teams</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
